/**
 * Cookie consent state machine — framework-agnostic primitives.
 *
 * - Source of truth: a first-party cookie scoped to `.sighthost.online` so the
 *   choice is SHARED across the marketing (www.) and portal (portal.) apps.
 *   This is required for Google Ads conversion tracking: the visitor accepts on
 *   www. but the conversion fires on portal. — localStorage is per-origin and
 *   would not carry the choice across. See
 *   `_bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md` (AD-4).
 * - localStorage is kept as a same-origin MIRROR so the native `storage` event
 *   still drives cross-tab sync.
 * - Pure functions; no React. The `useCookieConsent` hook in @sighthost/ui
 *   wraps these via `useSyncExternalStore` so consumers re-render on change.
 * - Same-tab sync uses a `CustomEvent('afrilink:cookie-consent-change')` we
 *   dispatch on every write.
 *
 * See `_bmad-output/MARKETING_IMPLEMENTATION_PLAN_2026-05-07.md` §M2.6 and
 * `memory/marketing_v2_architecture_decisions.md` Q3 for the original
 * rationale (superseded on the storage medium by AD-4 above).
 */

export const COOKIE_CONSENT_STORAGE_KEY = 'afrilink_cookie_consent';
export const COOKIE_CONSENT_CHANGE_EVENT = 'afrilink:cookie-consent-change';

/** Cross-subdomain cookie holding the consent choice (source of truth). */
const COOKIE_CONSENT_COOKIE = 'sighthost_cookie_consent';
/** 180 days — long enough to avoid nagging, short of "forever". */
const COOKIE_CONSENT_MAX_AGE = 60 * 60 * 24 * 180;

/**
 * Cookie domain that lets www. + portal. share the choice. Host-only (undefined)
 * on localhost / *.pages.dev previews, where a dotted apex domain can't be set.
 */
function consentCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  if (host === 'sighthost.online' || host.endsWith('.sighthost.online')) {
    return '.sighthost.online';
  }
  return undefined;
}

function readConsentCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)sighthost_cookie_consent=([^;]*)/);
  const raw = match?.[1];
  return raw != null ? decodeURIComponent(raw) : null;
}

function writeConsentCookie(value: string | null): void {
  if (typeof document === 'undefined') return;
  const domain = consentCookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  if (value === null) {
    document.cookie = `${COOKIE_CONSENT_COOKIE}=; path=/${domainAttr}; max-age=0; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${COOKIE_CONSENT_COOKIE}=${encodeURIComponent(
      value,
    )}; path=/${domainAttr}; max-age=${COOKIE_CONSENT_MAX_AGE}; SameSite=Lax${secure}`;
  }
}

/** Parse a persisted consent JSON blob defensively. */
function parseConsent(raw: string | null): ConsentChoice {
  if (!raw) return PENDING_CONSENT;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentChoice> & { status?: ConsentStatus };
    if (parsed.status === 'all' || parsed.status === 'essential') {
      return { status: parsed.status };
    }
    if (parsed.status === 'custom') {
      return {
        status: 'custom',
        analytics: 'analytics' in parsed ? !!parsed.analytics : false,
        marketing: 'marketing' in parsed ? !!parsed.marketing : false,
      };
    }
    return PENDING_CONSENT;
  } catch {
    return PENDING_CONSENT;
  }
}

export type ConsentStatus = 'pending' | 'all' | 'essential' | 'custom';

export type ConsentChoice =
  | { status: 'pending' }
  | { status: 'all' }
  | { status: 'essential' }
  | { status: 'custom'; analytics: boolean; marketing: boolean };

export const PENDING_CONSENT: ConsentChoice = { status: 'pending' };

export type ConsentCategory = 'analytics' | 'marketing';

/** Current raw persisted value: cookie (source of truth) then localStorage mirror. */
function currentRawConsent(): string | null {
  const fromCookie = readConsentCookie();
  if (fromCookie) return fromCookie;
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Snapshot cache — CRITICAL for useSyncExternalStore. getSnapshot MUST return a
// STABLE reference while the underlying value is unchanged; otherwise React
// re-renders forever ("Maximum update depth exceeded", error #185). We parse
// only when the raw string changes and otherwise return the cached object.
let cachedRawConsent: string | null = null;
let cachedConsentChoice: ConsentChoice = PENDING_CONSENT;

/**
 * Read the persisted consent choice. SSR-safe — returns `pending` when
 * `window` is unavailable. Prefers the cross-subdomain cookie (source of
 * truth) and falls back to the localStorage mirror. Returns a cached, stable
 * reference between reads so it is safe as a `useSyncExternalStore` snapshot.
 */
export function readConsent(): ConsentChoice {
  if (typeof window === 'undefined') return PENDING_CONSENT;
  const raw = currentRawConsent();
  if (raw === cachedRawConsent) return cachedConsentChoice;
  cachedRawConsent = raw;
  cachedConsentChoice = parseConsent(raw);
  return cachedConsentChoice;
}

/**
 * Persist a consent choice. Writes the cross-subdomain cookie (source of
 * truth) plus the localStorage mirror, then dispatches
 * `afrilink:cookie-consent-change` for same-tab subscribers; the native
 * `storage` event handles cross-tab sync. SSR-safe — no-op when `window` is
 * unavailable.
 */
export function writeConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return;
  if (choice.status === 'pending') {
    writeConsentCookie(null);
    try {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  } else {
    const serialised = JSON.stringify(choice);
    writeConsentCookie(serialised);
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, serialised);
    } catch {
      /* ignore */
    }
  }
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT));
}

/**
 * Returns true if the given non-essential category is permitted under the
 * current choice. Use this before mounting analytics/marketing scripts.
 */
export function isCategoryAllowed(choice: ConsentChoice, category: ConsentCategory): boolean {
  if (choice.status === 'all') return true;
  if (choice.status === 'essential' || choice.status === 'pending') return false;
  if (choice.status === 'custom') return choice[category];
  return false;
}
